/**
 * Maps industry selections to filtered services, offerings, and solutions
 * for the CommercialBrowseSidebar navigation.
 */
import { serviceLinks, industryLinks, crossIndustryLinks, industrySolutionCategories } from "@/components/navbar/navData";
import type { ServiceLink } from "@/components/navbar/navData";

export interface NavIndustry {
  label: string;
  href: string;
  color: string;
  isCrossIndustry?: boolean;
}

export interface NavOffering {
  title: string;
  href: string;
}

export interface NavServiceGroup {
  label: string;
  href: string;
  color: string;
  offerings: NavOffering[];
}

export interface NavSolution {
  label: string;
  href: string;
}

/** All selectable industries (main + cross-industry) */
export const navIndustries: NavIndustry[] = [
  ...industryLinks.map((l) => ({ label: l.label, href: l.href, color: l.color })),
];

/** Service entries without Audits (it's a standalone CTA, not a browsable service) */
export const navServices: Pick<ServiceLink, "label" | "href" | "color">[] = serviceLinks
  .filter((s) => s.label !== "Audits")
  .map((s) => ({ label: s.label, href: s.href, color: s.color }));

/**
 * Industry label → Supabase `industries` column value mapping.
 * The DB uses slightly different names than navData labels.
 */
const INDUSTRY_DB_MAP: Record<string, string[]> = {
  "Pharma & Biotech": ["Pharma & BioTech"],
  "Medical Devices": ["Medical Devices"],
  "In Vitro Diagnostics": ["In Vitro Diagnostics"],
  "PrecisionDx": ["In Vitro Diagnostics", "Pharma & BioTech"], // CDx bridges both
  "Combination Products": ["Pharma & BioTech", "Medical Devices"],
};

/**
 * Given an industry label, returns the matching DB industry filter values.
 */
export function getIndustryFilterValues(industryLabel: string): string[] {
  return INDUSTRY_DB_MAP[industryLabel] ?? [];
}

/**
 * Filter offerings (from Supabase query result) by industry label.
 */
export function filterOfferingsByIndustry(
  offerings: { title: string; href: string | null; industries: string[]; service_slug: string }[],
  industryLabel: string
): NavServiceGroup[] {
  const filterValues = getIndustryFilterValues(industryLabel);
  if (!filterValues.length) return [];

  // Group by service_slug
  const grouped = new Map<string, NavOffering[]>();

  for (const o of offerings) {
    if (!o.href) continue;
    const matches = filterValues.some((fv) => o.industries.includes(fv));
    if (!matches) continue;

    const existing = grouped.get(o.service_slug) ?? [];
    existing.push({ title: o.title, href: o.href });
    grouped.set(o.service_slug, existing);
  }

  // Nav href slug → DB service_slug mapping (where they differ)
  const SLUG_MAP: Record<string, string> = {
    "clinical-solutions": "clinical",
    "regulatory-toxicology": "toxicology",
  };

  // Map to NavServiceGroup using navServices for labels/colors
  return navServices
    .map((svc) => {
      const rawSlug = svc.href.replace("/en/services/", "");
      const dbSlug = SLUG_MAP[rawSlug] ?? rawSlug;
      const offerings = grouped.get(dbSlug) ?? [];
      if (!offerings.length) return null;
      return { label: svc.label, href: svc.href, color: svc.color, offerings };
    })
    .filter(Boolean) as NavServiceGroup[];
}

/**
 * Get solutions for a given industry label.
 */
export function getSolutionsForIndustry(industryLabel: string): NavSolution[] {
  // Map industry label to solution category matches
  const categoryMap: Record<string, string[]> = {
    "Pharma & Biotech": ["Pharma & Biotech Solutions", "Cross-Industry"],
    "Medical Devices": ["Medical Device & IVD Solutions"],
    "In Vitro Diagnostics": ["Medical Device & IVD Solutions", "Cross-Industry"],
    "PrecisionDx": ["Cross-Industry"],
    "Combination Products": ["Cross-Industry"],
  };

  const matchingCategories = categoryMap[industryLabel] ?? [];
  const solutions: NavSolution[] = [];

  for (const cat of industrySolutionCategories) {
    if (!matchingCategories.includes(cat.category)) continue;
    for (const item of cat.items) {
      const href = item.href ?? item.subLinks?.[0]?.href;
      if (href) {
        solutions.push({ label: item.label, href });
      }
    }
  }

  return solutions;
}
